import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { Trans, useTranslation } from 'react-i18next';
import { UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

type MutationOptions = UseMutationOptions<AxiosResponse<any>, unknown, any, unknown>;

type Args = {
    queryInvalidateFunction: () => void;
    ruleId?: string;
    mutationOptions: MutationOptions;
    mode: 'deleteTenantGroupOverride' | 'updateTenantGroupOverride';
    tenantGroupId: number | undefined;
    tenantGroupName?: string;
};

const useTenantGroupOverrideErrorHandler = (
    { mutationOptions, queryInvalidateFunction, mode, tenantGroupId, tenantGroupName }: Args = {
        mutationOptions: {},
        queryInvalidateFunction: () => {},
        mode: 'deleteTenantGroupOverride' || 'updateTenantGroupOverride',
        tenantGroupId: 0,
        tenantGroupName: ''
    }
) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { clearAll, addTechnicalErrorModal, addModal } = useReduxModal();
    const { ruleId } = useParams<{
        ruleId: string;
    }>();
    const { onError } = mutationOptions;
    const modalTitle = t('tenantOverrideNotSave');
    const isDeleteTenantGroupOverride = mode === 'deleteTenantGroupOverride';

    const errorHandler = React.useCallback(
        (error: any, variables?: any, context?: any) => {
            const handleNotFound = () => {
                if (isDeleteTenantGroupOverride) {
                    addTechnicalErrorModal(
                        '',
                        t('Tenant Group Override Deleted'),
                        () => {
                            queryInvalidateFunction();
                            clearAll();
                            history.push(`/tenants/tenant-group/${tenantGroupId}`);
                        },
                        {
                            status: 'error',
                            content: t('alreadyDeletedTenantGroupOverride')
                        }
                    );
                }
            };

            const handleTenantGroupDeleted = () => {
                addTechnicalErrorModal(
                    '',
                    t('Tenant Group Unavailable'),
                    () => {
                        queryInvalidateFunction();
                        clearAll();
                        history.push('/tenants');
                    },
                    {
                        status: 'error',
                        content: t('tenantGroupDeletedMsg', { tenantGroupName }),
                        cancelText: t('Return to Manage Tenants')
                    }
                );
            };

            const handleDefault = () => {
                addTechnicalErrorModal(
                    '',
                    modalTitle,
                    () => {
                        queryInvalidateFunction();
                        clearAll();
                        history.push(`/tenants/tenant-group/${tenantGroupId}`);
                    },
                    {
                        status: 'error',
                        content: <Trans i18nKey='tenantOverrideNotSaveMsg' />,
                        cancelText: t('Select Different Rule')
                    }
                );
            };

            const handleRuleNotFound = () => {
                if (
                    (error.response?.data?.length
                        ? error.response?.data?.[0]?.message
                        : error.response?.data?.message) ===
                    `The rule ${ruleId} does not exist in the reference rule revision`
                ) {
                    addModal({
                        id: 'update-override-rule-error',
                        status: 'warning',
                        title: t('Error Saving Override'),
                        content: t('Override Could Not Be Saved'),
                        showCloseIcon: false,
                        showCloseButton: false,
                        closeOnAction: true,
                        onActionText: 'Confirm',
                        onClose: () => {
                            history.replace(`/tenants/tenant-group/${tenantGroupId}`);
                        }
                    });
                } else {
                    handleDefault();
                }
            };

            switch (error?.response?.status || error?.status) {
                case 409:
                    handleTenantGroupDeleted();
                    break;
                case 404:
                    handleNotFound();
                    break;
                case 400:
                    handleRuleNotFound();
                    break;
                default:
                    handleDefault();
                    break;
            }

            onError?.(error, variables, context);
        },
        [
            onError,
            isDeleteTenantGroupOverride,
            queryInvalidateFunction,
            addTechnicalErrorModal,
            t,
            clearAll,
            history,
            modalTitle,
            tenantGroupId,
            tenantGroupName,
            ruleId,
            addModal
        ]
    );

    return errorHandler;
};

export default useTenantGroupOverrideErrorHandler;
